import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '@/seo/Seo';
import api from '@/api/client';
import { toast } from 'sonner';
import { HandHeart, ArrowLeft, CheckCircle2, Loader2, Calendar, MapPin } from 'lucide-react';

/* -----------------------------------------------------------------------
 * Option lists — kept in one place so both the form and the audit view
 * on the admin side stay in sync with the printed VBS Volunteer flyer.
 * --------------------------------------------------------------------- */

const BEFORE_VBS_OPTIONS = [
  'Praying',
  'Helping with administrative tasks',
  'Decorating classrooms',
  'Building sets',
  'Planning publicity',
  'Preparing craft materials',
  'Planning decorations',
  'Painting banners, backdrops, set',
  'Sewing',
];

const DURING_VBS_AREAS = [
  'Exploration Group', 'Crafts', 'Snacks', 'Games', 'Music', 'Missions', 'Nursery',
];

const OTHER_AREAS = [
  'Drama', 'Photographer', 'Sound/AV', 'Clean-up Crew', 'Teacher', 'Teaching Assistant',
];

const GROUP_LEVELS = [
  "0-3 years (God's Heritage)",
  "God's Treasure",
  'Holy Nation',
  'Royal Priesthood',
  'Chosen Generation',
];

const DAYS = [
  'Everyday',
  'If certain days please click the boxes below:',
  'Monday 3rd',
  'Tuesday 4th',
  'Wednesday 5th',
  'Thursday 6th',
  'Friday 7th',
  'Saturday 8th',
];

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  before_vbs: [],
  during_vbs: {},          // { 'Exploration Group': 'Leader' | 'Helper' | '' }
  other_areas: [],
  group_level_preference: [],
  days_available: [],
  consent_given: true,
};

/* --- small reusable field primitives (styled to match the existing MoJ forms) -- */

function TextField({ label, required, value, onChange, type = 'text', autoComplete, testId }) {
  return (
    <div>
      <label className="label-moj">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className="input-moj"
        data-testid={testId}
      />
    </div>
  );
}

function CheckboxList({ label, required, options, values, onToggle, testId }) {
  const chosenCount = values?.length || 0;
  const invalid = required && chosenCount === 0;
  return (
    <fieldset data-testid={testId}>
      <legend className="label-moj">
        {label} {required && <span className="text-red-600">*</span>}
      </legend>
      <div className="mt-1 space-y-2">
        {options.map((opt) => {
          const checked = values.includes(opt);
          return (
            <label
              key={opt}
              className={`flex items-center gap-3 px-3 py-2 border-2 rounded-lg cursor-pointer transition ${
                checked
                  ? 'border-moj-purple bg-moj-purple/5 text-moj-purple-deep font-semibold'
                  : 'border-moj-gold/30 hover:border-moj-purple/50'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(opt)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={`w-4 h-4 rounded border-2 flex-shrink-0 ${
                  checked ? 'border-moj-purple bg-moj-purple' : 'border-moj-purple/40'
                }`}
              >
                {checked && (
                  <svg viewBox="0 0 24 24" className="w-3 h-3 mx-auto mt-[1px] text-white">
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              <span className="text-sm">{opt}</span>
            </label>
          );
        })}
      </div>
      {/* Screen-reader-only validation hint; the browser also enforces `required` on submit via the hidden proxy input */}
      <input
        type="text"
        tabIndex={-1}
        aria-hidden
        required={required}
        value={chosenCount > 0 ? 'ok' : ''}
        onChange={() => {}}
        className="sr-only"
      />
      {invalid && (
        <p className="text-xs text-red-600 mt-1">Please choose at least one option.</p>
      )}
    </fieldset>
  );
}

function DuringVBSMatrix({ value, onChange }) {
  return (
    <fieldset data-testid="during-vbs-matrix">
      <legend className="label-moj">
        During VBS, I would like to help in one or more of the following areas:
      </legend>
      <div className="mt-2 overflow-x-auto rounded-lg border border-moj-gold/30">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-moj-cream-soft text-moj-purple-deep">
              <th className="text-left py-2 px-3 font-semibold w-1/2">Area</th>
              <th className="py-2 px-3 font-semibold">Leader</th>
              <th className="py-2 px-3 font-semibold">Helper</th>
            </tr>
          </thead>
          <tbody>
            {DURING_VBS_AREAS.map((area, idx) => (
              <tr
                key={area}
                className={idx % 2 === 0 ? 'bg-white' : 'bg-moj-cream-soft/40'}
              >
                <th scope="row" className="text-left py-2 px-3 font-normal text-moj-charcoal">
                  {area}:
                </th>
                {['Leader', 'Helper'].map((role) => {
                  const selected = value[area] === role;
                  return (
                    <td key={role} className="text-center py-2 px-3">
                      <label className="inline-flex items-center justify-center cursor-pointer">
                        <input
                          type="radio"
                          name={`during-${area}`}
                          value={role}
                          checked={selected}
                          onChange={() => onChange(area, role)}
                          className="sr-only"
                          data-testid={`during-${area.toLowerCase().replace(/\s+/g, '-')}-${role.toLowerCase()}`}
                        />
                        <span
                          aria-label={`${area} — ${role}`}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selected ? 'border-moj-purple' : 'border-moj-purple/40'
                          }`}
                        >
                          {selected && <span className="w-2.5 h-2.5 rounded-full bg-moj-purple" />}
                        </span>
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-moj-charcoal/60 mt-2">
        Optional — leave a row blank if you do not want to serve in that area. Selecting a
        different option replaces your previous choice for that row.
      </p>
    </fieldset>
  );
}

/* -------------------------------- Page ------------------------------- */

function useEventLookup(slug) {
  const [state, setState] = useState({ loading: true, event: null, error: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/events/${slug}/volunteer`);
        if (!cancelled) setState({ loading: false, event: data, error: null });
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        setState({
          loading: false,
          event: null,
          error: status === 404 ? 'Event not found' : 'Could not load event details.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);
  return state;
}

export default function VolunteerRegistration() {
  const { slug } = useParams();
  const { loading, event, error } = useEventLookup(slug);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const toggleIn = (key) => (opt) => {
    setForm((s) => {
      const set = new Set(s[key]);
      if (set.has(opt)) set.delete(opt);
      else set.add(opt);
      return { ...s, [key]: Array.from(set) };
    });
  };

  const pickDuring = (area, role) => {
    setForm((s) => {
      const next = { ...s.during_vbs };
      // clicking the same role again clears it (nice UX for optional matrix)
      if (next[area] === role) delete next[area];
      else next[area] = role;
      return { ...s, during_vbs: next };
    });
  };

  const canSubmit = useMemo(() => {
    return (
      form.name.trim() &&
      form.phone.trim() &&
      form.email.trim() &&
      form.before_vbs.length > 0 &&
      form.other_areas.length > 0 &&
      form.days_available.length > 0
    );
  }, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/volunteer`, form);
      setSubmittedId(data?.volunteer_id || 'ok');
      toast.success('Volunteer registration submitted — thank you!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  /* -------- Success screen -------- */
  if (submittedId) {
    return (
      <>
        <Seo title="Volunteer registration received" noindex />
        <section className="bg-moj-cream min-h-[70vh]">
          <div className="max-w-2xl mx-auto px-4 py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-moj-purple text-moj-gold flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={32} />
            </div>
            <h1 className="font-serif text-3xl text-moj-purple-deep mb-3">
              Thank you for volunteering!
            </h1>
            <p className="text-moj-charcoal/80 mb-2">
              Your registration for{' '}
              <span className="font-semibold text-moj-purple-deep">
                {event?.title || 'the Bible Camp'}
              </span>{' '}
              has been received. Our VBS coordinator will be in touch shortly with next
              steps.
            </p>
            <p className="text-sm text-moj-charcoal/60">
              Reference: <span className="font-mono">{submittedId}</span>
            </p>
            <div className="mt-8">
              <Link to="/" className="btn-primary" data-testid="volunteer-return-home-btn">
                <ArrowLeft size={16} /> Return to homepage
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  /* -------- Loading / not-found states -------- */
  if (loading) {
    return (
      <section className="bg-moj-cream min-h-[70vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-moj-purple-deep">
          <Loader2 className="animate-spin" size={20} /> Loading form…
        </div>
      </section>
    );
  }
  if (error || !event) {
    return (
      <>
        <Seo title="Volunteer form" noindex />
        <section className="bg-moj-cream min-h-[70vh]">
          <div className="max-w-xl mx-auto px-4 py-16 text-center">
            <h1 className="font-serif text-2xl text-moj-purple-deep mb-3">
              {error || 'Event not found'}
            </h1>
            <p className="text-moj-charcoal/70 mb-6">
              The volunteer form you&apos;re looking for isn&apos;t available. Please
              double-check the link with the VBS team.
            </p>
            <Link to="/" className="btn-primary">
              <ArrowLeft size={16} /> Return to homepage
            </Link>
          </div>
        </section>
      </>
    );
  }

  /* -------- Form -------- */
  return (
    <>
      <Seo title={`Volunteer — ${event.title}`} noindex />
      <section className="bg-moj-cream py-10">
        <div className="max-w-2xl mx-auto px-4">
          {/* Header card */}
          <div className="card-moj p-6 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl gradient-purple flex items-center justify-center">
                <HandHeart size={22} className="text-moj-gold" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-moj-gold-deep font-bold">
                  Ministry of Jesus, Dublin
                </p>
                <h1 className="font-serif text-2xl text-moj-purple-deep">
                  VBS | Volunteer Form
                </h1>
              </div>
            </div>
            <p className="text-moj-charcoal/80 text-sm">
              Thank you for offering to serve at{' '}
              <span className="font-semibold text-moj-purple-deep">{event.title}</span>.
              Please tell us how you&apos;d like to help — every gift matters.
            </p>
            <div className="flex flex-wrap gap-4 mt-4 text-xs text-moj-charcoal/70">
              {event.event_date && (
                <span className="inline-flex items-center gap-1">
                  <Calendar size={14} className="text-moj-gold-deep" />
                  {event.event_date}
                  {event.event_time ? ` @ ${event.event_time}` : ''}
                </span>
              )}
              {event.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={14} className="text-moj-gold-deep" /> {event.location}
                </span>
              )}
            </div>
          </div>

          {/* Form card */}
          <form
            onSubmit={onSubmit}
            className="card-moj p-6 space-y-6"
            data-testid="volunteer-form"
            noValidate={false}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField
                label="Name"
                required
                value={form.name}
                onChange={(v) => setForm((s) => ({ ...s, name: v }))}
                autoComplete="name"
                testId="volunteer-name"
              />
              <TextField
                label="Phone No"
                required
                value={form.phone}
                onChange={(v) => setForm((s) => ({ ...s, phone: v }))}
                autoComplete="tel"
                type="tel"
                testId="volunteer-phone"
              />
              <div className="sm:col-span-2">
                <TextField
                  label="E-mail"
                  required
                  value={form.email}
                  onChange={(v) => setForm((s) => ({ ...s, email: v }))}
                  autoComplete="email"
                  type="email"
                  testId="volunteer-email"
                />
              </div>
            </div>

            <CheckboxList
              label="Before VBS, I would like to help by:"
              required
              options={BEFORE_VBS_OPTIONS}
              values={form.before_vbs}
              onToggle={toggleIn('before_vbs')}
              testId="volunteer-before-vbs"
            />

            <DuringVBSMatrix value={form.during_vbs} onChange={pickDuring} />

            <CheckboxList
              label="Other Areas:"
              required
              options={OTHER_AREAS}
              values={form.other_areas}
              onToggle={toggleIn('other_areas')}
              testId="volunteer-other-areas"
            />

            <CheckboxList
              label="Group Level Preference:"
              options={GROUP_LEVELS}
              values={form.group_level_preference}
              onToggle={toggleIn('group_level_preference')}
              testId="volunteer-group-level"
            />

            <CheckboxList
              label="Days Available:"
              required
              options={DAYS}
              values={form.days_available}
              onToggle={toggleIn('days_available')}
              testId="volunteer-days-available"
            />

            <label className="flex items-start gap-3 text-sm text-moj-charcoal/80">
              <input
                type="checkbox"
                checked={form.consent_given}
                onChange={(e) =>
                  setForm((s) => ({ ...s, consent_given: e.target.checked }))
                }
                className="mt-1"
                data-testid="volunteer-consent"
              />
              <span>
                I consent to Ministry of Jesus storing the information above for the sole
                purpose of coordinating VBS volunteers. See our{' '}
                <Link to="/privacy" className="underline text-moj-purple hover:text-moj-purple-deep">
                  Privacy Notice
                </Link>
                .
              </span>
            </label>

            <div className="flex items-center justify-between gap-4 pt-2">
              <p className="text-xs text-moj-charcoal/60">
                <span className="text-red-600">*</span> Required
              </p>
              <button
                type="submit"
                disabled={!canSubmit || !form.consent_given || submitting}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="volunteer-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Submitting…
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
